import { ChangeEvent, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { Modal } from "@/components";
import { InputField } from "@/components/InputField/InputField";
import { Checkbox } from "@/components/Checkbox/Checkbox";
import { getAllErrors, renderErrorMessages } from "@/utils";

import { List } from "../schema/list";

interface ListOverviewModalsProps {
	/** The list a single-row action targets (delete / duplicate / archive / reset). */
	selectedList: List | null;

	showDeleteModal: boolean;
	onCloseDelete: () => void;
	onConfirmDelete: () => void;

	showDuplicateModal: boolean;
	onCloseDuplicate: () => void;
	onConfirmDuplicate: () => void;
	duplicateLoading: boolean;
	newName: string;
	onNewNameChange: (value: string) => void;
	copyCustomColumns: boolean;
	onToggleCopyCustomColumns: () => void;
	duplicateError: FetchBaseQueryError | SerializedError | undefined;

	showArchiveModal: boolean;
	onCloseArchive: () => void;
	onConfirmArchive: () => void;

	showDeleteListsModal: boolean;
	onCloseDeleteLists: () => void;
	onConfirmDeleteLists: () => void;

	showResetModal: boolean;
	onCloseReset: () => void;
	onConfirmReset: () => void;
	resetLoading: boolean;
}

/**
 * The confirmation modals for the List overview row/bulk actions (delete, duplicate, archive,
 * bulk-delete, reset). Extracted from {@link ListTable} as a pure presentational unit so that file
 * stays within the 500-line ratchet; all state and handlers continue to live in `ListTable`.
 */
export const ListOverviewModals: FunctionComponent<ListOverviewModalsProps> = ({
	selectedList,
	showDeleteModal,
	onCloseDelete,
	onConfirmDelete,
	showDuplicateModal,
	onCloseDuplicate,
	onConfirmDuplicate,
	duplicateLoading,
	newName,
	onNewNameChange,
	copyCustomColumns,
	onToggleCopyCustomColumns,
	duplicateError,
	showArchiveModal,
	onCloseArchive,
	onConfirmArchive,
	showDeleteListsModal,
	onCloseDeleteLists,
	onConfirmDeleteLists,
	showResetModal,
	onCloseReset,
	onConfirmReset,
	resetLoading,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`list.${key}`);

	return (
		<>
			{showDeleteModal && (
				<Modal
					handleClose={onCloseDelete}
					handleSave={onConfirmDelete}
					isOpened={showDeleteModal}
					submitButtonColor="error"
					submitButtonText={t("basics.delete")}
					title={ts("delete.title")}
				>
					<div className="text-sm text-gray-700 mb-4">{ts("delete.confirmation")}</div>
					<InputField
						isDisabled
						label={ts("name")}
						name="listName"
						value={selectedList?.name ?? ""}
					/>
				</Modal>
			)}
			{showDuplicateModal && (
				<Modal
					handleClose={onCloseDuplicate}
					handleSave={onConfirmDuplicate}
					isLoading={duplicateLoading}
					isOpened={showDuplicateModal}
					submitButtonColor="error"
					submitButtonText={t("basics.duplicate")}
					title={ts("duplicate.title")}
				>
					<div className="text-sm text-gray-700 mb-4">{ts("duplicate.confirmation")}</div>
					<InputField
						handleChange={(e: ChangeEvent<HTMLInputElement>) => {
							onNewNameChange(e.target.value);
						}}
						label={ts("newName")}
						name="newName"
						value={newName}
					/>
					<Checkbox isChecked={copyCustomColumns} onChange={onToggleCopyCustomColumns}>
						Do you want to copy the custom columns?
					</Checkbox>
					{getAllErrors(duplicateError, []).length
						? renderErrorMessages(getAllErrors(duplicateError, []))
						: null}
				</Modal>
			)}
			{showArchiveModal && (
				<Modal
					handleClose={onCloseArchive}
					handleSave={onConfirmArchive}
					isOpened={showArchiveModal}
					submitButtonColor="error"
					submitButtonText={selectedList?.archived ? t("basics.unarchive") : t("basics.archive")}
					title={selectedList?.archived ? ts("unarchive.title") : ts("archive.title")}
				>
					<div className="text-sm text-gray-700 mb-4">
						{selectedList?.archived ? ts("unarchive.confirmation") : ts("archive.confirmation")}
					</div>
					<InputField
						isDisabled
						label={ts("name")}
						name="listName"
						value={selectedList?.name ?? ""}
					/>
				</Modal>
			)}
			{showDeleteListsModal && (
				<Modal
					handleClose={onCloseDeleteLists}
					handleSave={onConfirmDeleteLists}
					isOpened={showDeleteListsModal}
					submitButtonColor="error"
					submitButtonText={t("basics.delete")}
					title={ts("delete.titleSelected")}
				>
					<div className="text-sm text-gray-700 mb-4">{ts("delete.confirmationSelected")}</div>
				</Modal>
			)}
			{showResetModal && (
				<Modal
					handleClose={onCloseReset}
					handleSave={onConfirmReset}
					isLoading={resetLoading}
					isOpened={showResetModal}
					submitButtonColor="error"
					submitButtonText={t("basics.reset")}
					title={ts("reset.title")}
				>
					<div className="text-sm text-gray-700 mb-4">{ts("reset.confirmation")}</div>
					<InputField
						isDisabled
						label={ts("name")}
						name="listName"
						value={selectedList?.name ?? ""}
					/>
				</Modal>
			)}
		</>
	);
};
